'use client';
import React from 'react';

const cards = [
  {
    tag: '[AppSec]',
    title: 'Application Security',
    subtitle: 'SAST · DAST · SCA · CVE Triage',
    features: [
      'SAST, DAST, and SCA integrated into your CI/CD pipelines',
      'CVE triage and enrichment, context-driven, not just CVSS scores',
      'Supply chain and dependency security across your full stack',
      'Business-logic vulnerabilities that automated scanners miss',
    ],
  },
  {
    tag: '[Compliance]',
    title: 'Compliance & Continuous Compliance',
    subtitle: 'SOC2 · ISO 27001 · DORA · GDPR',
    features: [
      'SOC2 Type I/II and ISO 27001:2022 readiness and certification',
      'DORA: ICT risk management, incident response, third-party oversight',
      'GDPR: data protection by design and technical control implementation',
      'Continuous control monitoring, evidence collected daily, not pre-audit',
    ],
  },
  {
    tag: '[Architecture]',
    title: 'Threat Modelling & Architecture',
    subtitle: 'Risk-led · Embedded · Continuous',
    features: [
      'Threat modelling for features, services, and infrastructure changes',
      'Security architecture review and zero-trust design',
      'Risk-led programmes tailored to your actual threat landscape',
      'Security embedded into your engineering and design process',
    ],
  },
  {
    tag: '[DevSecOps]',
    title: 'Continuous DevSecOps',
    subtitle: 'Embedded · Ongoing · Year-round',
    features: [
      'Senior engineers embedded in your sprints and standups',
      'Continuous monitoring, drift detection, and rapid remediation',
      'Security reporting that gives leadership real visibility',
      'Security culture built into your engineering practice',
    ],
  },
  {
    tag: '[IaC]',
    title: 'IaC & Pipeline Security',
    subtitle: 'Terraform · Pulumi · CloudFormation',
    features: [
      'IaC security for Terraform, Pulumi, CloudFormation, and Ansible',
      'Policy-as-code enforcement with OPA, Checkov, and custom rulesets',
      'Pipeline hardening: secrets management and supply chain controls',
      'Pre-merge security gates that stop misconfigurations at source',
    ],
  },
  {
    tag: '[Cloud]',
    title: 'Cloud & Platform Engineering',
    subtitle: 'AWS · Azure · GCP · Kubernetes',
    features: [
      'Secure cloud architecture design across AWS, Azure, and GCP',
      'Identity and access management: least-privilege and RBAC',
      'Network segmentation, micro-segmentation, and zero-trust access',
      'Kubernetes and container security: image scanning and runtime protection',
    ],
  },
];

export default function HybridSection() {
  return (
    <section className="bg-[#050a0f] border-t border-[#222] py-20 lg:py-28 relative overflow-hidden">
      {/* Background orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="orb orb-blue" style={{ width: '500px', height: '500px', top: '-100px', left: '-100px', opacity: 0.4 }} />
        <div className="orb orb-teal" style={{ width: '350px', height: '350px', bottom: '-50px', right: '10%', opacity: 0.3 }} />
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 mb-16">
          <div className="lg:w-1/2 fade-in-left">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">
              Our capabilities
            </p>
            <h2 className="font-heading text-[clamp(2rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight">
              Security that goes deeper than the audit.
            </h2>
          </div>
          <div className="lg:w-1/2 flex items-end fade-in-right" style={{ transitionDelay: '0.15s' }}>
            <p className="text-[#aaa] text-base leading-relaxed">
              TechSonar engineers are embedded into your organisation, delivering meaningful, impactful security across AppSec, compliance, threat modelling, IaC, and cloud platform engineering. Not box-ticking. Not fix-and-forget. Real security, every day of the year.
            </p>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards?.map((card, i) => (
            <div
              key={i}
              className="pixel-card relative border border-[#222] bg-[#181b1f] overflow-hidden"
            >
              {/* Top accent bar */}
              <div className="h-1 w-full bg-gradient-to-r from-[#80edfa] to-transparent" />

              {/* Content */}
              <div className="p-6">
                <div className="mb-5">
                  <p className="font-mono-brand text-[0.6rem] text-[#80edfa] uppercase tracking-widest mb-2">
                    {card?.tag}
                  </p>
                  <h3 className="font-heading text-lg text-[#f5f5f8] font-medium">
                    {card?.title}
                  </h3>
                  <p className="font-mono-brand text-[0.6rem] text-[#555] uppercase tracking-widest mt-1">
                    {card?.subtitle}
                  </p>
                </div>

                <ul className="flex flex-col gap-3">
                  {card?.features?.map((feature, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <img
                        src="https://cdn.prod.website-files.com/68a77090b95f85ab69076143/68fba43b700c01c8947096af_checkbox.svg"
                        alt=""
                        className="w-4 h-4 mt-0.5 flex-shrink-0"
                      />
                      <span className="text-sm text-[#aaa]">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
